import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Lock, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { reconcileRazorpayOrder } from "../lib/razorpayReconcile";
import { useAuth } from "../context/AuthContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { FEATURE_LABELS } from "../lib/featureAccess";
import { inrFull } from "../lib/format";

function getActivitySession() {
  try {
    let id = sessionStorage.getItem("estima_activity_session");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `as-${Date.now()}`);
      sessionStorage.setItem("estima_activity_session", id);
    }
    return id;
  } catch { return "anon"; }
}

/**
 * Renders when a user lacks access to the feature. Shows add-on price + "Pay & enable"
 * for Basic users, plus an "Upgrade to Pro" alternative.
 */
export default function LockedFeatureCard({ slug }) {
  const { user } = useAuth();
  const { access, refresh: refreshAccess } = useFeatureAccess();
  const { refresh: refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const handlerFiredRef = useRef(false);

  const addon = (access?.all_addons_catalog || []).find((a) => a.slug === slug);
  const label = FEATURE_LABELS[slug] || addon?.name || slug;
  const isBasic = access?.tier === "basic";

  // Track lock-card views so the backend can surface "addon opportunity" insights
  useEffect(() => {
    if (!user) return;
    api.post("/activity/event", {
      event_type: "lock_card_view",
      feature_slug: slug,
      page_path: window.location.pathname,
      session_id: getActivitySession(),
    }).catch(() => {});
  }, [user, slug]);

  const buyAddon = async (cycle) => {
    if (!isBasic) {
      toast("Only Basic users can buy add-ons. Subscribe to Basic first.");
      return;
    }
    setBusy(true);
    try {
      const Razorpay = await loadRazorpay();
      const { data: order } = await api.post("/billing/addon/create-order", {
        addon_slug: slug, cycle,
      });
      handlerFiredRef.current = false;
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Estima · Add-on",
        description: order.plan_label,
        order_id: order.order_id,
        prefill: { name: order.customer.name, email: order.customer.email },
        theme: { color: "#C85A32" },
        handler: async (resp) => {
          handlerFiredRef.current = true;
          try {
            await api.post("/billing/addon/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`${label} unlocked.`);
            await Promise.all([refreshAccess(), refreshUser()]);
          } catch (e) {
            const r = await reconcileRazorpayOrder("addon", resp.razorpay_order_id);
            if (r.ok) {
              toast.success(`${label} unlocked.`);
              await Promise.all([refreshAccess(), refreshUser()]);
            } else {
              toast.error(formatApiErrorDetail(e.response?.data?.detail));
            }
          } finally { setBusy(false); }
        },
        modal: {
          ondismiss: async () => {
            if (handlerFiredRef.current) { setBusy(false); return; }
            const r = await reconcileRazorpayOrder("addon", order.order_id);
            setBusy(false);
            if (r.ok) {
              toast.success(`Payment received — ${label} unlocked.`);
              await Promise.all([refreshAccess(), refreshUser()]);
            }
          },
        },
      };
      new Razorpay(opts).open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid={`locked-${slug}`}>
      <div className="card-flat p-10 max-w-xl w-full text-center">
        <div className="flex justify-center mb-5">
          <div className="h-14 w-14 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center">
            <Lock size={28} weight="duotone" className="text-[hsl(var(--secondary))]" />
          </div>
        </div>
        <div className="eyebrow mb-2">Locked feature</div>
        <h2 className="font-serif text-3xl mb-3">{label}</h2>
        <p className="text-muted-foreground text-sm mb-8">
          {isBasic
            ? `This feature isn't part of your Basic plan. Add it as a one-time add-on, or upgrade to Pro to unlock everything at once.`
            : user
              ? `This feature requires a Basic plan with this add-on, or a Pro plan.`
              : `Sign in and choose a plan to access this feature.`}
        </p>

        {addon && isBasic && (
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <button
              disabled={busy}
              onClick={() => buyAddon("monthly")}
              className="btn-ghost px-4 py-3 text-sm flex flex-col items-start gap-1"
              data-testid={`locked-${slug}-buy-monthly`}
            >
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Plus size={11} weight="bold" /> Add-on · Monthly
              </span>
              <span className="num-metric text-xl">{inrFull(addon.monthly)}</span>
              <span className="text-[11px] text-muted-foreground">per month, auto-renews</span>
            </button>
            <button
              disabled={busy}
              onClick={() => buyAddon("yearly")}
              className="btn-ghost px-4 py-3 text-sm flex flex-col items-start gap-1"
              data-testid={`locked-${slug}-buy-yearly`}
            >
              <span className="text-xs text-[hsl(var(--secondary))] inline-flex items-center gap-1.5">
                <Plus size={11} weight="bold" /> Add-on · Yearly · save ~17%
              </span>
              <span className="num-metric text-xl">{inrFull(addon.yearly)}</span>
              <span className="text-[11px] text-muted-foreground">per year, billed once</span>
            </button>
          </div>
        )}

        <Link
          to="/app/profile"
          className="btn-primary w-full px-4 py-3 text-sm inline-flex items-center justify-center gap-2"
          data-testid={`locked-${slug}-upgrade-pro`}
        >
          <Crown size={14} weight="duotone" /> Upgrade to Pro · everything unlocked
        </Link>

        {!isBasic && (
          <Link
            to="/app/profile"
            className="block mt-3 text-xs text-muted-foreground hover:text-foreground"
            data-testid={`locked-${slug}-see-plans`}
          >
            See all subscription options →
          </Link>
        )}
      </div>
    </div>
  );
}
